import { Components, Theme } from '@mui/material';

export type LelyTagType =
  | 'neutral'
  | 'information'
  | 'advice'
  | 'warning'
  | 'error'
  | 'success'
  | 'maintenance';

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    tag: true;
  }
}

const getInsetBorder = (color: string) => `inset 0 0 0 1px ${color}`;

const tagTypeStyle = (
  backgroundColor: string,
  color: string,
  boxShadow = 'none',
) => ({
  backgroundColor,
  color,
  boxShadow,
  '& .MuiChip-icon, & .icon-svg': { color },
});

const getTagTypeStyles = (theme: Theme) => ({
  '&[data-tag-type="neutral"]': tagTypeStyle(
    theme.palette.background.default,
    theme.palette.text.default,
    getInsetBorder(theme.palette.border.default),
  ),
  '&[data-tag-type="information"]': tagTypeStyle(
    theme.palette.background.information,
    theme.palette.text.light,
  ),
  '&[data-tag-type="advice"]': tagTypeStyle(
    theme.palette.background.advice,
    theme.palette.text.default,
  ),
  '&[data-tag-type="warning"]': tagTypeStyle(
    theme.palette.background.warning,
    theme.palette.text.light,
  ),
  '&[data-tag-type="error"]': tagTypeStyle(
    theme.palette.background.error,
    theme.palette.text.light,
  ),
  '&[data-tag-type="success"]': tagTypeStyle(
    theme.palette.background.success,
    theme.palette.text.light,
  ),
  '&[data-tag-type="maintenance"]': tagTypeStyle(
    theme.palette.background.maintenance,
    theme.palette.text.light,
  ),
});

export const getTagChipVariants = (
  theme: Theme,
): NonNullable<Components['MuiChip']>['variants'] => [
  {
    props: { variant: 'tag' },
    style: {
      padding: `${theme.lelySpacing.XS} ${theme.lelySpacing.SM}`,
      cursor: 'default',
      pointerEvents: 'none',

      '& .MuiChip-icon': {
        margin: 0,
        width: theme.lelySpacing._2XL,
        height: theme.lelySpacing._2XL,
      },
      '& .icon-svg': {
        width: theme.lelySpacing._2XL,
        height: theme.lelySpacing._2XL,
      },

      '&[data-dense="true"]': {
        padding: `0 ${theme.lelySpacing.XS}`,
        gap: theme.lelySpacing._2XS,
        height: theme.lelySpacing.XL,

        '& .MuiChip-icon, & .icon-svg': {
          width: theme.lelySpacing.XL,
          height: theme.lelySpacing.XL,
        },
      },

      ...getTagTypeStyles(theme),
    },
  },
];
